import { defineVariants } from "@/runtime/style-engine/define-variants";

/**
 * Tailwind-like class variants for `<e-menu>`.
 *
 * Migrated from menu.css with full support for:
 * - Vertical orientation with collapsible state
 * - Open/closed mobile menu states
 * - Responsive breakpoints (lg, max-lg)
 * - Nested elements (header, logo, list, footer, etc.)
 */
export const menuClasses = defineVariants({
  base: `sticky top-0 w-full h-full max-h-screen flex flex-col justify-between bg-(--color-card)`,
  menu: `z-10 flex flex-col min-h-0`,
  header: `flex justify-between lg:mb-6`,
  headerOptions: `ml-auto flex items-center gap-2 justify-end lg:hidden`,
  logo: `h-8 inline-block`,
  list: `flex flex-col gap-2 overflow-x-hidden overflow-y-auto min-h-0 px-0.5`,
  leading: ``,
  trailing: `flex flex-col mb-6`,
  footerWrapper: `flex flex-wrap gap-2 justify-between items-center z-10 pt-4 border-t border-t-(--color-elevation-300)`,
  footer: `flex flex-wrap gap-2 items-center justify-center`,
  collapsibleButton: `max-lg:hidden`,
  backgroundButton: `max-lg:hidden absolute inset-0 hover:cursor-e-resize`,
  floating: `z-50 pointer-events-auto w-[14rem]`,
  floatingTooltip: `whitespace-nowrap font-heading lowercase text-sm`,
  floatingHeader: `px-1 py-1 text-xs font-heading lowercase font-semibold text-(--color-foreground)/60 tracking-wide`,
  floatingSubmenu: `flex flex-col gap-0.5 max-h-[60vh] overflow-y-auto overscroll-contain`,
  floatingSubmenuItem: `flex items-center gap-2 px-3 py-1.5 rounded-lg cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-(--color-foreground) transition-colors hover:bg-(--color-neutral)/10 font-heading lowercase no-underline text-current text-sm`,
  floatingSubmenuIcon: `flex-shrink-0`,
  floatingSubmenuContent: `flex-1 truncate`,

  variants: {
    /**
     * Menu orientation
     */
    orientation: {
      vertical: "",
      horizontal: "",
    },

    /**
     * Collapsed state (desktop)
     */
    collapsed: {
      true: "",
      false: "",
    },

    /**
     * Open state (mobile)
     */
    open: {
      true: "",
      false: "",
    },
  },

  defaults: {
    orientation: "vertical",
    collapsed: "false",
    open: "false",
  },

  compoundVariants: [
    // Vertical orientation base padding
    {
      orientation: "vertical",
      class: {
        base: "p-4",
      },
    },

    // Collapsed state - header adjustments
    {
      collapsed: "true",
      class: {
        header: "lg:justify-center lg:items-center lg:mb-4 lg:mt-2",
        logo: "size-8 aspect-square",
        trailing: "hidden",
        base: "lg:overflow-hidden",
        footer: "lg:[&>slot::slotted(*)]:w-12 lg:[&>slot::slotted(*)]:h-12 lg:[&>slot::slotted(*)]:overflow-hidden",
        footerWrapper: "lg:flex-col lg:flex-nowrap lg:items-center lg:justify-center lg:gap-1",
      },
    },

    {
      collapsed: "false",
      class: {
        logo: "w-[178px]",
      },
    },

    // Open state (mobile)
    {
      open: "true",
      class: {
        base: "max-lg:h-screen",
        header: "max-lg:mb-6",
      },
    },

    // Closed state (mobile) - hide elements
    {
      open: "false",
      class: {
        header: "max-lg:mb-0",
        leading: "max-lg:hidden",
        list: "max-lg:hidden",
        trailing: "max-lg:hidden",
        footerWrapper: "max-lg:hidden",
      },
    },
  ],
});

/** Active styling for floating submenu items (applied conditionally in Menu.vue). */
export const floatingSubmenuItemActive = `!bg-(--color-accent)/15 !text-(--color-accent) !font-bold`;

/**
 * Tailwind-like class variants for `<e-menu-item>`.
 *
 * Migrated from menu-item.css with full support for:
 * - Label type menu items
 * - Regular menu items with icons
 * - Submenu items (nested)
 * - Details/summary collapsible items
 * - Active and selected states
 * - Disabled state
 */
export const menuItemClasses = defineVariants({
  base: `flex items-center rounded-xl transition ease-out cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-(--color-foreground)`,
  label: `text-xs font-bold text-(--color-neutral-500) pb-1`,
  icon: `p-3`,
  title: `font-heading lowercase`,
  details: `rounded-xl outline-none`,
  detailsSummary: `flex items-center [&>*:not(.e-menu-icon)]:w-full [&_.e-menu-icon]:p-3 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-(--color-foreground) rounded-xl`,
  detailsContent: `flex flex-col p-0 pr-2 pb-2 pl-2`,
  detailsTrailingIcon: `transition-transform`,
  submenuItem: `font-heading lowercase grid grid-cols-[16px_1fr_16px] items-center w-full gap-3 rounded hover:text-(--color-accent) p-1 pl-3 [&>*:not(.e-menu-icon)]:col-start-2 [&_.e-menu-icon]:text-sm [&_.e-menu-icon]:p-0 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-(--color-foreground)`,
  disabled: `opacity-75 cursor-not-allowed`,

  elementPrefix: "e-menu-item",

  variants: {
    /**
     * Menu item type
     */
    type: {
      label: "",
      link: "",
      trigger: "",
    },

    /**
     * Active state (router active)
     */
    active: {
      true: "",
      false: "",
    },

    /**
     * Selected state
     */
    selected: {
      true: "",
      false: "",
    },

    /**
     * Disabled state
     */
    disabled: {
      true: "",
      false: "",
    },

    /**
     * Is submenu item
     */
    isSubItem: {
      true: "",
      false: "",
    },

    /**
     * Details open state
     */
    detailsOpen: {
      true: "",
      false: "",
    },

    /**
     * Collapsed state (from parent menu).
     * True when the sidebar is collapsed — items become icon-only squares.
     */
    collapsed: {
      true: "",
      false: "",
    },
  },

  defaults: {
    type: "link",
    active: "false",
    selected: "false",
    disabled: "false",
    isSubItem: "false",
    detailsOpen: "false",
    collapsed: "false",
  },

  compoundVariants: [
    // Label type uses label styles
    {
      type: "label",
      class: {
        base: "", // Remove default menu-item styles
      },
    },

    // Active state styling
    {
      active: "true",
      isSubItem: "false",
      class: {
        base: "text-(--color-accent) font-bold [&_slot:not([name])]:text-(--color-accent) [&_slot:not([name])]:font-bold [&_slot:not([name])::slotted(*)]:text-(--color-accent) [&_slot:not([name])::slotted(*)]:font-bold",
        title: "text-(--color-accent) font-bold",
      },
    },
    {
      active: "true",
      isSubItem: "true",
      class: {
        base: "text-(--color-accent)",
        title: "text-(--color-accent) font-bold",
      },
    },
    {
      selected: "true",
      class: {
        base: "text-(--color-accent)",
        title: "text-(--color-accent)",
      },
    },
    {
      disabled: "true",
      class: {
        base: "opacity-75 cursor-not-allowed",
      },
    },
    {
      isSubItem: "true",
      class: {
        title: [
          {
            when: { element: "submenuItem", state: "hover" },
            classes: "text-(--color-primary)",
          },
        ],
        submenuItem: "hover:bg-(--color-card) rounded-lg",
      },
    },
    {
      detailsOpen: "true",
      class: {
        details: "bg-(--color-background)",
        detailsSummary: "text-(--color-accent)",
        detailsTrailingIcon: "rotate-45",
      },
    },
    {
      detailsOpen: "false",
      class: {
        details: "hover:bg-(--color-background)",
        detailsTrailingIcon: {
          when: { element: "detailsSummary", state: "hover" },
          classes: "rotate-45",
        },
      },
    },

    // Base item layout adjustments and hover state
    {
      type: "link",
      isSubItem: "false",
      class: {
        base: 'hover:bg-(--color-background) [&>*:not(.e-menu-icon):not(slot[name="trailing"])]:w-full [&>slot:not([name])]:flex-1 [&>slot:not([name])]:min-w-0 [&>slot[name="trailing"]]:ml-auto [&>slot[name="trailing"]]:flex-0 [&>slot[name="trailing"]]:inline-flex [&>slot[name="trailing"]]:items-center [&_.e-menu-item-details-trailing-icon]:ml-auto [&_.e-menu-item-details-trailing-icon]:flex-0 [&_.e-menu-item-details-trailing-icon]:inline-flex [&_.e-menu-item-details-trailing-icon]:items-center',
      },
    },

    // Slot styling for default content
    {
      type: "link",
      class: {
        base: "[&_slot:not([name])]:font-heading [&_slot:not([name])]:lowercase [&_slot:not([name])::slotted(*)]:font-heading [&_slot:not([name])::slotted(*)]:lowercase",
      },
    },

    // Collapsed: top-level item becomes an icon-only square. `lg:` so mobile
    // (where collapse is not applied) keeps the full-width layout. Applies to
    // both the `<a>` base and the `<details>/<summary>` wrapper of expandable
    // items, since either branch renders depending on whether the item has
    // children.
    {
      collapsed: "true",
      isSubItem: "false",
      class: {
        base: "lg:w-12 lg:h-12 lg:aspect-square lg:p-0 lg:justify-center",
        details: "lg:w-12 lg:h-12 lg:aspect-square",
        detailsSummary: "lg:w-12 lg:h-12 lg:aspect-square lg:p-0 lg:justify-center",
      },
    },
  ],
});
